import React, { useState, useEffect } from 'react';
import { useSettingsStore } from '../store/useSettingsStore';
import { useDataStore } from '../store/useDataStore';
import { Type, Layout, Languages, Trash2, AlertTriangle, CheckCircle, Save } from 'lucide-react';
import { cleanupInvalidMedia } from '../utils/cleanupMedia';
import { ConfirmDialog } from './UI/ConfirmDialog';
import type { Settings } from '../types/data';

export const SettingsView: React.FC = () => {
  const { settings, updateSettings, saveSettings, loadSettings } = useSettingsStore();
  const { userUuid } = useDataStore();
  const [localSettings, setLocalSettings] = useState<Settings>(settings);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<{
    total: number;
    deleted: number;
    failed: number;
  } | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // 监听 userUuid 变化，切换用户时重新加载设置
  useEffect(() => {
    if (userUuid) {
      loadSettings()
        .then(() => {
          console.log('[SettingsView] 用户切换后设置已重新加载')
        })
        .catch(err => {
          console.error('[SettingsView] 用户切换后加载设置失败:', err)
        })
    }
  }, [userUuid, loadSettings])

  // 初始化本地设置
  useEffect(() => {
    setLocalSettings(settings);
    setHasUnsavedChanges(false);
  }, [settings]);

  // 检查是否有未保存的更改
  useEffect(() => {
    const changed = JSON.stringify(localSettings) !== JSON.stringify(settings);
    setHasUnsavedChanges(changed);
    if (!changed) {
      setSaveSuccess(false);
    }
  }, [localSettings, settings]);

  const handleToggle = (key: keyof Settings) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleFontSize = (delta: number) => {
    setLocalSettings(prev => ({
      ...prev,
      fontSize: Math.max(12, Math.min(32, prev.fontSize + delta))
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    
    try {
      // 更新 store 中的设置（不自动保存）
      updateSettings(localSettings, false);
      
      // 保存到 localStorage 和后端
      await saveSettings();
      
      // 同步到原生 JS
      const win = window as any;
      if (win.renderCanvas) win.renderCanvas();
      
      // 重新加载设置以确保同步（这会从后端获取最新设置）
      await loadSettings();
      
      // 更新本地设置以匹配 store（清除未保存更改状态）
      // 注意：loadSettings 会更新 store，所以我们需要从 store 获取最新值
      const updatedSettings = useSettingsStore.getState().settings;
      setLocalSettings(updatedSettings);
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (error) {
      console.error('保存设置失败:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCleanupInvalidMedia = async () => {
    setIsCleaning(true);
    setCleanupResult(null);
    
    try {
      const result = await cleanupInvalidMedia(userUuid || '');
      setCleanupResult(result);
      
      if (result.deleted > 0) {
        // 清理成功后，可以触发重新渲染
        const win = window as any;
        if (win.renderCanvas) {
          win.renderCanvas();
        }
      }
    } catch (error: any) {
      console.error('清理失败:', error);
      setCleanupResult({
        total: 0,
        deleted: 0,
        failed: 1
      });
    } finally {
      setIsCleaning(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto h-full overflow-y-auto custom-scrollbar">
      <div className="mb-10">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-black text-gray-800 tracking-tight">设置</h1>
          <button
            onClick={handleSave}
            disabled={!hasUnsavedChanges || isSaving}
            className={`px-6 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 ${
              hasUnsavedChanges && !isSaving
                ? 'bg-[#ff5c5c] text-white hover:bg-[#ff4a4a] shadow-lg hover:shadow-xl active:scale-95'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                保存中...
              </>
            ) : saveSuccess ? (
              <>
                <CheckCircle size={18} />
                已保存
              </>
            ) : (
              <>
                <Save size={18} />
                保存设置
              </>
            )}
          </button>
        </div>
        <p className="text-gray-400 font-medium">⚙️ 个性化你的画布编辑体验</p>
        {hasUnsavedChanges && (
          <div className="mt-3 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-700 flex items-center gap-2">
            <AlertTriangle size={16} />
            您有未保存的更改
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* Appearance Group */}
        <div className="bg-white rounded-[40px] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.02)] border border-pink-50">
          <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Layout size={20} className="text-[#ff5c5c]" />
            外观设置
          </h3>
          
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-gray-700">显示边框</div>
                <div className="text-sm text-gray-400">在画布项目周围显示装饰性边框</div>
              </div>
              <button 
                onClick={() => handleToggle('showBorder')}
                className={`w-14 h-8 rounded-full transition-all relative ${localSettings.showBorder ? 'bg-[#ff5c5c]' : 'bg-gray-200'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all ${localSettings.showBorder ? 'left-7' : 'left-1'}`} />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-gray-700">座右铭模式</div>
                <div className="text-sm text-gray-400">更简约的文本显示风格</div>
              </div>
              <button 
                onClick={() => handleToggle('mottoStyle')}
                className={`w-14 h-8 rounded-full transition-all relative ${localSettings.mottoStyle ? 'bg-[#ff5c5c]' : 'bg-gray-200'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all ${localSettings.mottoStyle ? 'left-7' : 'left-1'}`} />
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-gray-700">隐藏图片边框</div>
                <div className="text-sm text-gray-400">使图片与背景更融合</div>
              </div>
              <button 
                onClick={() => handleToggle('hideImageBorder')}
                className={`w-14 h-8 rounded-full transition-all relative ${localSettings.hideImageBorder ? 'bg-[#ff5c5c]' : 'bg-gray-200'}`}
              >
                <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all ${localSettings.hideImageBorder ? 'left-7' : 'left-1'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Typography Group */}
        <div className="bg-white rounded-[40px] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.02)] border border-pink-50">
          <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Type size={20} className="text-blue-500" />
            文字设置
          </h3>
          
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-gray-700">默认字号</div>
                <div className="text-sm text-gray-400">新建文本框时的初始大小</div>
              </div>
              <div className="flex items-center gap-4 bg-gray-50 p-1.5 rounded-2xl">
                 <button onClick={() => handleFontSize(-1)} className="w-10 h-10 bg-white rounded-xl shadow-sm hover:text-[#ff5c5c] font-bold transition-all active:scale-90">-</button>
                 <span className="w-8 text-center font-bold text-gray-600">{localSettings.fontSize}</span>
                 <button onClick={() => handleFontSize(1)} className="w-10 h-10 bg-white rounded-xl shadow-sm hover:text-[#ff5c5c] font-bold transition-all active:scale-90">+</button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-gray-700">界面语言</div>
                <div className="text-sm text-gray-400">当前：{localSettings.language === 'zh-CN' ? '简体中文' : 'English'}</div>
              </div>
              <button className="px-4 py-2 bg-gray-50 text-gray-400 rounded-xl text-sm font-bold flex items-center gap-2 cursor-not-allowed">
                <Languages size={16} />
                切换语言 (待实现)
              </button>
            </div>
          </div>
        </div>

        {/* Data Management Group */}
        <div className="bg-white rounded-[40px] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.02)] border border-pink-50">
          <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Trash2 size={20} className="text-orange-500" />
            数据管理
          </h3>
          
          <div className="space-y-6">
            <div>
              <div className="mb-3">
                <div className="font-bold text-gray-700 mb-1">清理无效媒体文件</div>
                <div className="text-sm text-gray-400">
                  查找并删除 media 表中没有在 items 表中使用的记录，释放存储空间
                </div>
              </div>
              
              {cleanupResult && (
                <div className={`mb-4 p-4 rounded-xl border ${
                  cleanupResult.deleted > 0 
                    ? 'bg-green-50 border-green-200' 
                    : cleanupResult.failed > 0
                    ? 'bg-red-50 border-red-200'
                    : 'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-start gap-3">
                    {cleanupResult.deleted > 0 ? (
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                    ) : cleanupResult.failed > 0 ? (
                      <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                    ) : (
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                    )}
                    <div className="flex-1">
                      <div className="font-medium text-gray-800 mb-1">
                        {cleanupResult.deleted > 0 
                          ? '清理完成' 
                          : cleanupResult.failed > 0
                          ? '清理失败'
                          : '没有无效记录'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {cleanupResult.deleted > 0 ? (
                          <>已删除 <span className="font-bold text-green-700">{cleanupResult.deleted}</span> 条无效记录（共 {cleanupResult.total} 条）</>
                        ) : cleanupResult.failed > 0 ? (
                          <>清理过程中发生错误，请重试</>
                        ) : (
                          <>media 表中的所有记录都在使用中，无需清理</>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <button
                onClick={() => setShowConfirmDialog(true)}
                disabled={isCleaning}
                className="w-full px-6 py-3 bg-orange-500 text-white rounded-xl hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed font-bold transition-colors flex items-center justify-center gap-2"
              >
                {isCleaning ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    清理中...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    开始清理
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 确认对话框 */}
      <ConfirmDialog
        visible={showConfirmDialog}
        title="确认清理"
        message="此操作将删除所有无效的媒体文件记录，这些文件在项目中已经不再使用。此操作不可撤销，确定要继续吗？"
        confirmText="确认清理"
        cancelText="取消"
        dangerMode={true}
        onConfirm={() => {
          setShowConfirmDialog(false);
          handleCleanupInvalidMedia();
        }}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </div>
  );
};
